import { DatePicker, Form, Input, Modal, notification, TimePicker } from 'antd';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CheckTimeModel, CheckTimeReq } from 'models/workSchedule';
import useCreateCheckInAttendanceLog from 'hooks/query/workSchedule/useCreateCheckInAttendanceLog';
import useCreateCheckOutAttendanceLog from 'hooks/query/workSchedule/useCreateCheckOutAttendanceLog';
import useUpdateCheckTimeAttendanceLog from 'hooks/query/workSchedule/UseUpdateCheckTimeAttendanceLog';
import { UserModel } from 'models/users';
import { UserData } from 'utils/helpers/encrypt-decrypt-user';
import { useQueryClient } from 'react-query';


interface AddCheckTimeProps {
  openAddCheckTime: { open: boolean; type: string };
  setOpenAddCheckTime: (openAddCheckTime: { open: boolean; type: string }) => void;
  selectedAttendance: CheckTimeModel | null;
  setSelectedAttendance: (selectedAttendance: CheckTimeModel | null) => void;
  setUser: Dispatch<SetStateAction<UserModel | null>>
}

const AddCheckTime = ({ openAddCheckTime, setOpenAddCheckTime, selectedAttendance, setSelectedAttendance,  setUser }: AddCheckTimeProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient()
  const responseCreateCheckInAttendanceLog = useCreateCheckInAttendanceLog();
  const responseCreateCheckOutAttendanceLog = useCreateCheckOutAttendanceLog();
  const responseUpdateCheckTimeAttendanceLog = useUpdateCheckTimeAttendanceLog();

  useEffect(() => {
  if (!openAddCheckTime.open) return;
  if (selectedAttendance) {
      form.setFieldsValue({
          checkin_time: selectedAttendance.checkin_time
            ? dayjs(selectedAttendance.checkin_time).format("YYYY-MM-DD HH:mm:ss") 
            : undefined,

          checkout_time: selectedAttendance.checkout_time
            ? dayjs(selectedAttendance.checkout_time).format("YYYY-MM-DDTHH:mm")
            : undefined,
          note: selectedAttendance.note
      });
  } 
  else {
  form.resetFields();
  }
  }, [selectedAttendance, openAddCheckTime.open, form]);

  const onFinish = async (values: any) => {
    const payload : CheckTimeReq = { ...values };
    if (payload.note === undefined || payload.note === null) {
      delete payload.note;
    }
    if(payload.checkin_time) {
      payload.checkin_time = payload.checkin_time.replace("T", " ");
    }
    if (payload.checkout_time) {
      payload.checkout_time = payload.checkout_time.replace("T", " ");
    }
    if(selectedAttendance) {
      if (payload.checkin_time) {
        payload.checkin_time = dayjs(payload.checkin_time).format("YYYY-MM-DD HH:mm")
      }
      if (payload.checkout_time) {
        payload.checkout_time = dayjs(payload.checkout_time).format("YYYY-MM-DD HH:mm");
      }
        responseUpdateCheckTimeAttendanceLog
          .mutateAsync({data: payload, id: selectedAttendance.id})
          .then(() => {
            queryClient.invalidateQueries({
            queryKey: ['attendance-log'],
            });
            notification.open({
                type: 'success',
                message: t('check_time_updated_successfully'),
            });
            setUser(prev => {
              if (!prev) return prev;
              const updated: UserModel = {
                ...prev,
                checked_in: true,
              };

              UserData.Encrypt(updated);
              return updated;
            });
            handleClose();
          }
        )
          .catch(() => {});
    }
    else {
      if(openAddCheckTime.type === 'check_in') {
        responseCreateCheckInAttendanceLog
          .mutateAsync(payload)
          .then(() => {
            queryClient.invalidateQueries({
            queryKey: ['attendance-log'],
            });
            notification.open({
                type: 'success',
                message: t('check_in_successfully'),
            });
            setUser(prev => {
              if (!prev) return prev;
              const updated: UserModel = {
                ...prev,
                checked_in: true,
              };

              UserData.Encrypt(updated);
              return updated;
            });
            handleClose();
          }
        )
          .catch(() => {});
      }
      else if(openAddCheckTime.type === 'check_out') {
        responseCreateCheckOutAttendanceLog
          .mutateAsync(payload)
          .then(() => {
            queryClient.invalidateQueries({
            queryKey: ['attendance-log'],
            });
            notification.open({
                type: 'success',
                message: t('check_out_successfully'),
            });
            setUser(prev => {
              if (!prev) return prev;
              const updated: UserModel = {
                ...prev,
                checked_out: true,
              };

              UserData.Encrypt(updated);
              return updated;
            });
            handleClose();
          })
          .catch(() => {});
      }
    }
  };

  const handleClose = () => {
    setOpenAddCheckTime({ open: false, type: '' });
    form.resetFields();
  };

  return (
    <Modal
      title={selectedAttendance ? t('edit_check_time') : t('add_check_time')}
      open={openAddCheckTime.open}
      centered
      destroyOnClose
      onCancel={handleClose}
      closable={{ 'aria-label': 'Custom Close Button' }}
      okButtonProps={{ loading: responseCreateCheckInAttendanceLog.isLoading || responseCreateCheckOutAttendanceLog.isLoading || responseUpdateCheckTimeAttendanceLog.isLoading }}
      onOk={() => form.submit()}
    >
      <Form form={form} onFinish={onFinish} labelCol={{ span: 24 }}>
        {(openAddCheckTime.type === 'check_in' || openAddCheckTime.type === 'edit') && (
          <div style={{ marginBottom: '30px' }}>
            <p>{t('technician_checkin_time')}</p>
            <Form.Item
                name="checkin_time"
                rules={[{ required: true, message: t('please_enter_check_in') }]}
            >
                <Input 
                type="datetime-local" 
                />
            </Form.Item>
          </div>
        )}

        {(openAddCheckTime.type === 'check_out' || openAddCheckTime.type === 'edit') && (
          <div style={{ marginBottom: '30px' }}>
            <p>{t('technician_checkout_time')}</p>
            <Form.Item
                name="checkout_time"
                rules={[{ required: true, message: t('please_enter_check_out') }]}
            >
                <Input 
                type="datetime-local"
                />
            </Form.Item>
          </div>
        )}
        <Form.Item
          label={t('note')}
          name="note"
          style={{ marginBottom: '10px' }}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCheckTime;
